import {SlctFormat, SlctInternalProps, SlctOption, SlctProps} from 'module/Slct/type/SlctType'
import React, {FocusEventHandler, KeyboardEvent, useCallback, useEffect, useLayoutEffect, useMemo} from 'react'
import {CircularProgress} from '@mui/material'
import {log} from 'common/log'
import {getOptionLabel} from 'module/Slct/util/getOptionLabel'
import {Key} from 'util/Key'
import {Clear} from '@mui/icons-material'
import {memoAddAction} from 'module/Memo/action/MemoAction'
import {useDispatch} from 'react-redux'
import {cssCFInputBorder, cssCFInputBorderFocussed} from 'module/CustomField/style/cssCFInput'
import {
	cssSlctChildBase,
	cssSlctClearButton,
	cssSlctDisplayValue,
	cssSlctEllipsis,
	cssSlctInput,
	cssSlctInputLoadingWrapper,
	cssSlctPlaceholder,
	cssSlctPlaceholderText,
	cssSlctSingleChild,
	cssSlctWrapper,
} from 'module/Slct/style/SlctStyle'
import {useSlctOptions} from 'module/Slct/hook/useSlctOptions'
import {getNextCursor} from 'module/Slct/util/getNextCursor'
import {isMemorizable} from 'module/Memo/type/Memorizable'
import {slctSelectInputValue} from 'module/Slct/util/slctSelectInputValue'
import {isFirefox} from 'react-device-detect'
import {focusNextFormElement} from 'util/view/focusNextFormElement'
import {SetEvent} from 'type/SetCb'
import {useSlctProps} from 'module/Slct/hook/useSlctProps'
import {useSlctChangeState} from 'module/Slct/hook/useSlctChangeState'
import {useSlctSharedCallbacks} from 'module/Slct/hook/useSlctSharedCallbacks'
import {SlctMenuComponent} from 'module/Slct/component/SlctMenuComponent'
import {css} from '@emotion/react'
import {getTextAlignCSS} from 'module/CustomField/style/CFAlignmentStyle'
import {getSlctColor} from 'module/Slct/util/GetSlctColor'

/* use memo functions to pass props to this component */
export const Slct = React.memo(function Slct<ModelType extends object | void, ValueType extends string | number>(props: SlctProps<ModelType, ValueType>) {
	const dispatch = useDispatch()
	let {
		value, onChange: _onChange,
		options: optionsUnfiltered,
		filterOptions,
		onChangeState,
		displayFormat = SlctFormat.ValueLabel, showClearButton,
		preventCloseMenuOnSelect, menuWidth, menuHeight,
		disabled, loading,
		placeholderHTML = '', autoFocus, tabIndex = 0, align,
		menuPortal,
	} = props

	if (value === undefined) value = null

	const {
		byValue,
		inputValue, setInputValue,
		cursor, setCursor,
		focus, setFocus,
		menuOpen, setMenuOpen,
		memoKey, memoSize, memoAutoAdd,
		hasMenu,
		ref,
		refInput, setRefInput,
		refMenu,
		refList,
	} = useSlctProps(props)

	const onChange = useCallback((o: SlctOption<ModelType, ValueType> | null, event?: SetEvent) => {
		log.trace('Slct onChange', o)
		if (disabled || o?.disabled) return;
		if (!preventCloseMenuOnSelect) {
			setMenuOpen(false)
			setCursor(null)
			if (event) focusNextFormElement(1, {inputEl: refInput.current, event, focusNextOnlyInForm: true})
		}
		setInputValue(o && (o.value || o.value === 0) ? o.value + '' : '')
		_onChange(o)
		setTimeout(() => slctSelectInputValue(refInput), 20)
	}, [_onChange, refInput.current, disabled, preventCloseMenuOnSelect, setCursor, setMenuOpen])

	const valueObj: SlctOption<ModelType, ValueType> | null = useMemo(() => {
		return (value || value === 0) && (byValue?.[value] || {value} as SlctOption<ModelType, ValueType>) || null
	}, [value, byValue])

	const {options, valueIndex} = useSlctOptions({
		memoKey,
		optionsUnfiltered,
		filterOptions,
		value,
		valueObj,
		inputValue,
		byValue,
		filterOnInputValueEqualsValue: false,
	})
	const maxIndex = options.length - 1

	useSlctChangeState(onChangeState, inputValue, focus)

	const {
		onClickInput,
		onChangeInput,
		onKeyUp,
	} = useSlctSharedCallbacks(setMenuOpen, setInputValue, refInput)

	const onFocus: FocusEventHandler<HTMLDivElement> = useCallback((evt) => {
		log.trace('Slct onInputFocus', evt)
		setInputValue(value || value === 0 ? value + '' : '')
		setFocus(true)
		setMenuOpen(true)
		setCursor(null)
	}, [value, setFocus, setMenuOpen, setCursor])

	const onBlur: FocusEventHandler<HTMLElement> = useCallback((event) => {
		// needs to get delayed because click-onChange would get reset by this with wrong input value
		// input value of state not used, because it could have changed after execution. instead refInput.current.value is used
		const focusedMenu = refMenu.current?.contains(event.relatedTarget)
		const focusedInput = refInput.current?.contains(event.relatedTarget)
		if (focusedMenu || focusedInput) {
			log.trace('Slct onInputBlur cancel', event)
			event.preventDefault()
			event.stopPropagation()
			refInput.current?.focus()
			if (isFirefox) {
				setTimeout(() => {
					if (document.activeElement !== refInput.current) {
						// firefox does not call blur on blur, when cancelled before? -> recheck after cancel call
						setMenuOpen(false)
						setCursor(null)
						setFocus(false)
					}
				}, 1)
			}
			return;
		}
		log.trace('Slct onInputBlur blur', event, value, inputValue)
		// clicked / tabbed outside
		setMenuOpen(false)
		setCursor(null)
		setFocus(false)
		//set value from input. onChange (via click on option)j may have set the value into input
		const iv = inputValue.trim()
		if (
			!iv ?
				(value || value === 0)
				:
				iv !== value
		) {
			const optionFound = iv && byValue[iv as ValueType]
			onChange(iv ? optionFound || {value: iv, label: ''} as SlctOption<ModelType, ValueType> : null, event)
			if (optionFound && memoKey && memoSize && memoAutoAdd && isMemorizable(optionFound)) {
				dispatch(memoAddAction({memoKey, memoSize, element: optionFound}))
			}
		}
	}, [refInput.current, refMenu.current, onChange, value, inputValue, byValue, options, memoKey, memoSize, memoAutoAdd, setFocus, setMenuOpen, setCursor])

	const onClear = useCallback(() => {
		onChange(null)
		refInput.current?.focus()
	}, [onChange, refInput.current])

	const onKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
		log.trace('Slct onKeyDown', event)
		if ((event.key === Key.Tab || event.key === Key.Enter) && cursor !== null) {
			const o = options[cursor]
			if (o?.value || o?.value === 0) {
				setInputValue(o.value + '')
				onChange(o, event)
			}
			return;
		} else if (event.key === Key.Enter && cursor === null) {
			// set current input value as value (maybe in dq, maybe not)
			onChange({value: event.currentTarget.value} as SlctOption<ModelType, ValueType>, event)
			return;
		} else if (event.key === Key.Escape) {
			setInputValue(value || value === 0 ? value + '' : '')
			setTimeout(() =>
					refInput.current && window.document.activeElement === refInput.current && refInput.current.blur()
				, 10)
			return;
		}
		if (event.ctrlKey || event.altKey || event.shiftKey) return;
		const nextCursor = getNextCursor(event, cursor === null && valueIndex > -1 ? valueIndex : cursor, maxIndex)
		if (nextCursor !== null) refList.current?.scrollToItem(nextCursor, "smart")
		setCursor(nextCursor)
		log.debug('Slct set cursor / scroll to item', value, cursor, valueIndex)
	}, [refInput.current, refList.current, onChange, maxIndex, cursor, value, valueIndex, setCursor])

	const itemData: SlctInternalProps<ModelType, ValueType> = {
		...props,
		// new, internal props
		inputValue,
		focus,
		cursor, setCursor,
		//overridden before
		displayFormat,
		options,
		onClickOption: onChange,
		refInput,
	}

	const displayValue = getOptionLabel(displayFormat, valueObj)

	useEffect(() => {
		log.trace('Slct value changed', value)
		setInputValue(!value && value !== 0 ? '' : value + '')
	}, [value])

	useLayoutEffect(() => slctSelectInputValue(refInput), [refInput.current, focus, value])

	useEffect(() => {
		if (!(menuOpen && valueIndex > -1 && inputValue == value)) return
		// menu opened, scroll to current value
		refList.current?.scrollToItem(valueIndex, "start")
		log.debug('Slct scroll to item', menuOpen, value, inputValue, valueIndex)
	}, [refList.current, menuOpen, value, inputValue, valueIndex])

	let {background, color} = getSlctColor({disabled, valueObj, value, focus})

	return <>
		<div
			className={'SlctWrapper'}
			data-cursor-key-needs-modifier={hasMenu}
			css={[
				cssSlctWrapper,
				css`
					background: ${background};
					color: ${color};
				`,
				focus ? cssCFInputBorderFocussed : cssCFInputBorder,
			]}
			onFocus={onFocus}
			ref={ref}
		>
			{(!focus || !inputValue) && <div
				className={'SlctPlaceholder'}
				css={[
					cssSlctChildBase,
					cssSlctSingleChild,
					cssSlctPlaceholder,
					cssSlctEllipsis,
					getTextAlignCSS(align),
				]}
			>
				{!value && value !== 0 || !inputValue ?
					<div css={[cssSlctPlaceholderText, cssSlctEllipsis]} dangerouslySetInnerHTML={{__html: placeholderHTML || ''}}/>
					:
					<div
						css={[
							cssSlctDisplayValue,
							cssSlctEllipsis,
						]}
					>{displayValue}</div>
				}
			</div>
			}
			<input
				className={'SlctInput'}
				css={[
					cssSlctChildBase,
					cssSlctSingleChild,
					cssSlctInput,
					getTextAlignCSS(align),
				]}
				ref={setRefInput}
				autoFocus={autoFocus}
				tabIndex={tabIndex}
				value={focus ? inputValue : ''}
				onChange={onChangeInput}
				onBlur={onBlur}
				onKeyUp={onKeyUp}
				onKeyDown={onKeyDown}
				onClick={onClickInput}
				disabled={disabled}
			/>
			{loading &&
				<div className={'SlctLoading'} css={cssSlctInputLoadingWrapper}>
					<CircularProgress size={'inherit'} color={'inherit'}/>
				</div>
			}
			{showClearButton && value && !disabled &&
				<div
					css={cssSlctClearButton}
					onClick={onClear}
				>
					<Clear fontSize={'inherit'}/>
				</div>
			}
		</div>
		<SlctMenuComponent
			menuOpen={menuOpen}
			loading={loading}
			tabIndex={tabIndex}
			itemData={itemData}
			menuWidth={menuWidth}
			menuHeight={menuHeight}
			refWrapper={ref}
			refInput={refInput}
			refMenu={refMenu}
			refList={refList}
			menuPortal={menuPortal}
		/>
	</>
})